import { SIGNATURE_LIMITS } from "@probetalab/contracts";

/**
 * Turning a drawing or a photo into the signature the report prints (migration 0011): black ink
 * on transparency, cropped to the ink, at most 1200 × 400 px and 300 KB. The pixel work is pure
 * (testable without a canvas); the canvas glue at the bottom runs only in the browser.
 *
 * A drawing is already ink on white: grayscale, Otsu's threshold, done. A phone photo is not:
 * its paper is lit unevenly (a hand or the phone casts a soft shadow over part of the sheet), and
 * one global threshold turns the shadow into ink — the pilot lab's first photo came out as a black
 * box (2026-11-02). So a photo is first flattened: the paper's own brightness is estimated per
 * region (a coarse grid of local maxima, smoothed and interpolated) and every pixel is divided by
 * it, which leaves paper near white everywhere and ink dark wherever it sits. Then Otsu's
 * threshold, specks dropped, cropped to the ink. Ink keeps a soft edge (alpha by darkness) so the
 * printed stroke is not jagged. If what is left still covers too much of its own box it is not a
 * signature (deep shadow, a dark sheet), and the caller asks for a better photo instead.
 */

export interface Pixels {
  data: Uint8ClampedArray<ArrayBuffer>;
  width: number;
  height: number;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Luma of each pixel composited over white (a transparent pixel is paper), 0 = black. */
export function grayscale(img: Pixels): Uint8Array {
  const out = new Uint8Array(img.width * img.height);
  for (let i = 0; i < out.length; i++) {
    const r = img.data[i * 4]!;
    const g = img.data[i * 4 + 1]!;
    const b = img.data[i * 4 + 2]!;
    const a = img.data[i * 4 + 3]! / 255;
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    out[i] = Math.round(255 - a * (255 - luma));
  }
  return out;
}

/** Otsu's threshold over a gray image: the level that best separates ink from paper (below it is ink). */
export function otsuThreshold(gray: Uint8Array): number {
  const hist = new Array<number>(256).fill(0);
  for (const v of gray) hist[v]!++;
  const total = gray.length;
  let sumAll = 0;
  for (let t = 0; t < 256; t++) sumAll += t * hist[t]!;
  let sumB = 0;
  let wB = 0;
  let best = 0;
  let threshold = 127;
  for (let t = 0; t < 256; t++) {
    wB += hist[t]!;
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t]!;
    const mB = sumB / wB;
    const mF = (sumAll - sumB) / wF;
    const between = wB * wF * (mB - mF) ** 2;
    if (between > best) {
      best = between;
      // Midway between the two class means, not the split level itself: with a clean bimodal
      // image every level between ink and paper scores the same, and the first one is the ink's.
      threshold = Math.round((mB + mF) / 2);
    }
  }
  return threshold;
}

/**
 * Ink alpha per pixel: 0 for paper, up to 255 for ink, ramping over the darker half below the
 * threshold. A blank image (nothing darker than near-white) has no ink at all.
 */
export function inkAlpha(gray: Uint8Array, threshold = otsuThreshold(gray)): Uint8Array {
  const t = Math.min(threshold, 230);
  const out = new Uint8Array(gray.length);
  for (let i = 0; i < gray.length; i++) {
    const v = gray[i]!;
    if (v >= t) continue;
    out[i] = Math.min(255, Math.round((255 * (t - v)) / Math.max(1, t * 0.5)));
  }
  return out;
}

/** Bounding box of every pixel with ink, or null when there is none. */
export function inkBounds(alpha: Uint8Array, width: number, height: number, minAlpha = 24): Box | null {
  let x0 = width;
  let y0 = height;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (alpha[y * width + x]! < minAlpha) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  return x1 < 0 ? null : { x: x0, y: y0, width: x1 - x0 + 1, height: y1 - y0 + 1 };
}

/** The box grown by `pad` on every side, clamped to the image. */
export function padBox(box: Box, pad: number, width: number, height: number): Box {
  const x = Math.max(0, box.x - pad);
  const y = Math.max(0, box.y - pad);
  return { x, y, width: Math.min(width, box.x + box.width + pad) - x, height: Math.min(height, box.y + box.height + pad) - y };
}

/** Scale (≤ 1) that fits width × height inside the caps. */
export function fitScale(width: number, height: number, maxWidth = SIGNATURE_LIMITS.maxWidth, maxHeight = SIGNATURE_LIMITS.maxHeight): number {
  return Math.min(1, maxWidth / width, maxHeight / height);
}

/** Black ink with the given alpha, cropped to `box`: the RGBA pixels of the final signature. */
export function cropInk(alpha: Uint8Array, width: number, box: Box): Pixels {
  const data = new Uint8ClampedArray(box.width * box.height * 4);
  for (let y = 0; y < box.height; y++) {
    for (let x = 0; x < box.width; x++) {
      data[(y * box.width + x) * 4 + 3] = alpha[(box.y + y) * width + box.x + x]!;
    }
  }
  return { data, width: box.width, height: box.height };
}

/**
 * The paper's brightness around every pixel: the brightest value of each `block`-sided tile,
 * spread to the neighbouring tiles (so a tile filled by a thick stroke still sees paper), averaged
 * with them to soften the steps, and interpolated back to full resolution.
 */
export function estimateBackground(gray: Uint8Array, width: number, height: number, block: number): Uint8Array {
  const gw = Math.ceil(width / block);
  const gh = Math.ceil(height / block);
  const tile = new Float32Array(gw * gh);
  for (let y = 0; y < height; y++) {
    const row = Math.floor(y / block) * gw;
    for (let x = 0; x < width; x++) {
      const v = gray[y * width + x]!;
      const k = row + Math.floor(x / block);
      if (v > tile[k]!) tile[k] = v;
    }
  }
  const neighbourhood = (src: Float32Array, reduce: (vals: number[]) => number) => {
    const out = new Float32Array(src.length);
    for (let gy = 0; gy < gh; gy++) {
      for (let gx = 0; gx < gw; gx++) {
        const vals: number[] = [];
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nx = gx + dx;
            const ny = gy + dy;
            if (nx >= 0 && ny >= 0 && nx < gw && ny < gh) vals.push(src[ny * gw + nx]!);
          }
        }
        out[gy * gw + gx] = reduce(vals);
      }
    }
    return out;
  };
  const grid = neighbourhood(
    neighbourhood(tile, (v) => Math.max(...v)),
    (v) => v.reduce((a, b) => a + b, 0) / v.length,
  );
  // Bilinear interpolation between tile centres.
  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    const fy = Math.min(gh - 1, Math.max(0, (y + 0.5) / block - 0.5));
    const y0 = Math.floor(fy);
    const y1 = Math.min(gh - 1, y0 + 1);
    const ty = fy - y0;
    for (let x = 0; x < width; x++) {
      const fx = Math.min(gw - 1, Math.max(0, (x + 0.5) / block - 0.5));
      const x0 = Math.floor(fx);
      const x1 = Math.min(gw - 1, x0 + 1);
      const tx = fx - x0;
      const top = grid[y0 * gw + x0]! * (1 - tx) + grid[y0 * gw + x1]! * tx;
      const bottom = grid[y1 * gw + x0]! * (1 - tx) + grid[y1 * gw + x1]! * tx;
      out[y * width + x] = Math.round(top * (1 - ty) + bottom * ty);
    }
  }
  return out;
}

/** Each pixel divided by its paper brightness: paper ends near white under shadow or light alike. */
export function flattenIllumination(gray: Uint8Array, width: number, height: number, block = Math.max(12, Math.round(Math.max(width, height) / 48))): Uint8Array {
  const bg = estimateBackground(gray, width, height, block);
  const out = new Uint8Array(gray.length);
  for (let i = 0; i < gray.length; i++) out[i] = Math.min(255, Math.round((255 * gray[i]!) / Math.max(1, bg[i]!)));
  return out;
}

/**
 * Removes (in place) every 8-connected group of ink smaller than `minArea` pixels or never
 * reaching `minPeak` alpha: dust, paper grain, a faint shadow edge. Real strokes are long and
 * have a dark core.
 */
export function dropSpecks(alpha: Uint8Array, width: number, height: number, minArea: number, minPeak = 128): void {
  const seen = new Uint8Array(alpha.length);
  const stack = new Int32Array(alpha.length);
  const members: number[] = [];
  for (let start = 0; start < alpha.length; start++) {
    if (alpha[start] === 0 || seen[start]) continue;
    members.length = 0;
    let peak = 0;
    let top = 0;
    stack[top++] = start;
    seen[start] = 1;
    while (top > 0) {
      const i = stack[--top]!;
      members.push(i);
      if (alpha[i]! > peak) peak = alpha[i]!;
      const x = i % width;
      const y = (i - x) / width;
      for (let dy = -1; dy <= 1; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= height) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= width) continue;
          const j = ny * width + nx;
          if (alpha[j] === 0 || seen[j]) continue;
          seen[j] = 1;
          stack[top++] = j;
        }
      }
    }
    if (members.length < minArea || peak < minPeak) for (const i of members) alpha[i] = 0;
  }
}

/** Share of the box's pixels that are solid ink (alpha ≥ 128). A signature is mostly paper. */
export function inkCoverage(alpha: Uint8Array, width: number, box: Box): number {
  let solid = 0;
  for (let y = box.y; y < box.y + box.height; y++) {
    for (let x = box.x; x < box.x + box.width; x++) if (alpha[y * width + x]! >= 128) solid++;
  }
  return solid / Math.max(1, box.width * box.height);
}

/** Above this share of solid ink in its own box, a photo's "signature" is a shadow or a dark sheet. */
export const MAX_PHOTO_COVERAGE = 0.25;

/** Brightest level a flattened photo may call ink: the paper sits near 255 after flattening. */
const PHOTO_MAX_THRESHOLD = 200;

export type SignatureSource = "photo" | "drawing";
export type SignatureProblem = "empty" | "unclear";
export type Extraction = { ink: Pixels } | { problem: SignatureProblem };

/** Ink alpha of every pixel of the uncropped image (0 = paper), for a photo or a drawing. */
export function signatureAlpha(img: Pixels, source: SignatureSource): Uint8Array {
  const { width, height } = img;
  const gray = grayscale(img);
  if (source === "drawing") return inkAlpha(gray);
  const flat = flattenIllumination(gray, width, height);
  const alpha = inkAlpha(flat, Math.min(otsuThreshold(flat), PHOTO_MAX_THRESHOLD));
  dropSpecks(alpha, width, height, Math.max(8, Math.round((width * height) / 80_000)));
  return alpha;
}

/**
 * The whole pure pipeline: pixels in, cropped black-on-transparent pixels out, or why not —
 * `empty` (no ink at all) or `unclear` (a photo whose ink covers an implausible share of its box).
 * A drawing skips the flattening and the coverage guard: its background is already white, and a
 * filled shape drawn on purpose is the user's call.
 */
export function extractSignature(img: Pixels, source: SignatureSource = "photo"): Extraction {
  const { width, height } = img;
  const alpha = signatureAlpha(img, source);
  const bounds = inkBounds(alpha, width, height);
  if (!bounds) return { problem: "empty" };
  const pad = Math.max(4, Math.round(Math.max(bounds.width, bounds.height) * 0.02));
  const box = padBox(bounds, pad, width, height);
  if (source === "photo" && inkCoverage(alpha, width, box) > MAX_PHOTO_COVERAGE) return { problem: "unclear" };
  return { ink: cropInk(alpha, width, box) };
}

// ---- browser glue (canvas) -------------------------------------------------------------------

/** Largest side a photo is reduced to before processing: enough detail, bounded work on a tablet. */
const WORK_SIDE = 1800;

function canvas(width: number, height: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(width));
  c.height = Math.max(1, Math.round(height));
  return c;
}

function toPngBlob(c: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => c.toBlob((b) => resolve(b), "image/png"));
}

export type SignatureResult = { blob: Blob } | { problem: SignatureProblem };

/**
 * Runs the pipeline on any drawable source (the drawing canvas, a decoded photo) and returns a
 * PNG that meets the caps, or the problem that stopped it. Shrinks until it fits 300 KB.
 */
export async function prepareSignature(source: CanvasImageSource, width: number, height: number, kind: SignatureSource): Promise<SignatureResult> {
  const s = Math.min(1, WORK_SIDE / Math.max(width, height));
  const work = canvas(width * s, height * s);
  const ctx = work.getContext("2d", { willReadFrequently: true });
  if (!ctx) return { problem: "empty" };
  ctx.drawImage(source, 0, 0, work.width, work.height);
  const extraction = extractSignature(ctx.getImageData(0, 0, work.width, work.height), kind);
  if ("problem" in extraction) return extraction;
  const { ink } = extraction;
  const cropped = canvas(ink.width, ink.height);
  cropped.getContext("2d")!.putImageData(new ImageData(ink.data, ink.width, ink.height), 0, 0);
  let scale = fitScale(ink.width, ink.height);
  for (let attempt = 0; attempt < 6; attempt++) {
    const out = canvas(ink.width * scale, ink.height * scale);
    const octx = out.getContext("2d")!;
    octx.imageSmoothingQuality = "high";
    octx.drawImage(cropped, 0, 0, out.width, out.height);
    const blob = await toPngBlob(out);
    if (blob && blob.size <= SIGNATURE_LIMITS.maxBytes) return { blob };
    scale *= 0.75;
  }
  // Still over 300 KB at a fraction of the size: so much ink that it is not a clean signature.
  return { problem: "unclear" };
}

/** Decodes a photo (respecting its EXIF orientation) and prepares it as a signature. */
export async function signatureFromFile(file: File): Promise<SignatureResult> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    return await prepareSignature(bitmap, bitmap.width, bitmap.height, "photo");
  } finally {
    bitmap.close();
  }
}

/** Base64 of a blob, without the data-URL prefix (the `signature.set` payload). */
export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}
